import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Clock, Tag, ArrowRight, BookOpen } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { blogPosts } from '../data/blogPosts';

const BlogIndexPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-950" data-testid="blog-index-page">
      <Helmet>
        <title>Blog | Aloha Tequila & Arts Festival 2026 — Tequila Guides & Hawaii Festival Tips</title>
        <meta
          name="description"
          content="Tequila and festival guides from the Aloha Tequila & Arts Festival team. Read what to expect at the festival, tequila 101, and the best things to do in Oahu June 18–21, 2026."
        />
        <meta name="keywords" content="aloha tequila festival blog, tequila guide hawaii, tequila beginner guide, oahu festival tips" />
        <link rel="canonical" href="https://www.alohatequilafest.com/blog" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.alohatequilafest.com/' },
            { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://www.alohatequilafest.com/blog' }
          ]
        })}</script>
      </Helmet>

      <Header />

      <main className="pt-28 sm:pt-32 pb-20">
        <div className="max-w-6xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-500/10 border border-pink-500/30 mb-4">
              <BookOpen className="w-4 h-4 text-pink-400" />
              <span className="text-pink-300 text-sm font-medium tracking-wider uppercase">Festival Blog</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 via-orange-400 to-yellow-400 mb-4">
              Tequila & Festival Guides
            </h1>
            <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto">
              Tequila 101, festival guides, and everything you need to make the most of June 20, 2026 in Waikiki.
            </p>
          </div>

          {/* Posts grid */}
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            {blogPosts.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                data-testid={`blog-card-${post.slug}`}
                className="group bg-white/5 hover:bg-white/[0.07] border border-white/10 hover:border-pink-500/40 rounded-3xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={post.coverImage}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 sm:p-7">
                  <div className="flex items-center gap-3 text-white/50 text-xs mb-3">
                    <span className="inline-flex items-center gap-1 text-pink-400">
                      <Tag className="w-3 h-3" />
                      {post.tag}
                    </span>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {post.readTime}
                    </span>
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-3 group-hover:text-pink-400 transition-colors leading-tight">
                    {post.title}
                  </h2>
                  <p className="text-white/70 text-sm sm:text-base leading-relaxed mb-4">{post.excerpt}</p>
                  <span className="inline-flex items-center gap-1 text-pink-400 font-semibold text-sm">
                    Read post
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogIndexPage;
